/**
 * Frontend visibility evaluator (Phase F, §2.4, decisions C5/C6).
 *
 * Mirrors backend `com.kerkton.orna.service.form.VisibilityEvaluator` (Groovy)
 * case-for-case. See `orna-spring/docs/house-check-request/evaluator-parity-cases.json`
 * for the shared parity corpus.
 *
 * Rules:
 *   - Walk sections → fields in document order.
 *   - Field with no `showIf` → visible.
 *   - Field with `showIf` → visible iff:
 *       parent.visible && String(values[parent.key]) === String(showIf.equals)
 *   - Forward `showIf` reference (parent not yet seen) → hidden. Safe default;
 *     schema validator rejects this at rule 10 on save.
 *   - TABLE fields are leaves — column keys are NOT included in the result.
 *   - Insertion order of the returned map matches document order (Map literal
 *     / plain object iteration order in JS preserves insertion).
 */

import type { FormSectionData, FormFieldData } from '@/services/api-types';

/**
 * @param sections sections_json structure (may be null/undefined — treated as empty)
 * @param values current field values keyed by field key (may be null/undefined)
 * @returns fieldKey → isVisible, in document order
 */
export function evaluateVisibility(
  sections: FormSectionData[] | null | undefined,
  values: Record<string, unknown> | null | undefined,
): Record<string, boolean> {
  const visibility: Record<string, boolean> = {};
  if (sections == null) return visibility;
  const vals: Record<string, unknown> = values == null ? {} : values;

  for (const section of sections) {
    if (section == null) continue;
    const fields = (section as { fields?: unknown }).fields;
    if (!Array.isArray(fields)) continue;

    for (const fieldObj of fields as FormFieldData[]) {
      if (fieldObj == null || typeof fieldObj !== 'object') continue;
      const keyRaw = (fieldObj as { key?: unknown }).key;
      if (typeof keyRaw !== 'string' || keyRaw.length === 0) continue;
      const key = keyRaw;

      const showIfRaw = (fieldObj as { showIf?: unknown }).showIf;
      if (showIfRaw == null || typeof showIfRaw !== 'object') {
        visibility[key] = true;
        continue;
      }

      const refKeyRaw = (showIfRaw as { key?: unknown }).key;
      if (typeof refKeyRaw !== 'string') {
        // Malformed showIf — safe default is hidden.
        visibility[key] = false;
        continue;
      }

      const refKey = refKeyRaw;
      // Forward reference (parent not yet seen) → default hidden.
      const parentVisible = Object.prototype.hasOwnProperty.call(visibility, refKey)
        ? visibility[refKey]
        : false;

      const expected = (showIfRaw as { equals?: unknown }).equals;
      const actual = vals[refKey];
      const expectedStr = expected == null ? null : String(expected);
      const actualStr = actual == null ? null : String(actual);
      const parentMatches = expectedStr != null && expectedStr === actualStr;

      visibility[key] = parentVisible && parentMatches;
    }
  }
  return visibility;
}

/**
 * Returns a new object containing only the entries from `values` whose key is
 * NOT explicitly marked false in `visibility`. Keys not present in the
 * visibility map (e.g. TABLE-column values, truly unknown keys) pass through.
 *
 * C6 — hidden values are cleared on save.
 */
export function clearHidden(
  values: Record<string, unknown> | null | undefined,
  visibility: Record<string, boolean> | null | undefined,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  if (values == null) return result;
  const vis: Record<string, boolean> = visibility == null ? {} : visibility;

  for (const key of Object.keys(values)) {
    const v = vis[key];
    if (v === false) {
      // Explicitly hidden → drop.
      continue;
    }
    result[key] = values[key];
  }
  return result;
}
